import { create } from 'zustand';
import { isDemoGuestAccessToken } from '@/lib/auth/demoGuest';
import { apiFetch } from '@/lib/http';
import { parseLimits, type ReportedLimit } from '@/lib/limits/types';

export type FeatureMaturity = 'alpha' | 'beta' | 'released';

/** Mirrors admin.FeatureState on the Go side (lowerCamelCase json tags). */
export interface FeatureState {
  id: string;
  name: string;
  enabled: boolean;
  maturity: FeatureMaturity;
  billable: boolean;
}

interface FeaturesResponse {
  bundle: string;
  features: FeatureState[];
  /**
   * Chat vision-input availability (2026-07-07). Server-computed: the
   * `LVDR_CHAT_VISION_ENABLED` flag AND a sovereign vision backend present in
   * this deployment's Meridian catalog. Folded into the features bootstrap
   * payload (not a separate endpoint) and fails soft to `false` — when absent
   * or on a load error the composer hides/disables the attachment affordance
   * rather than letting a user upload into a dead end. See
   * docs/plans/2026-07-07-chat-vision-input-design.md §4.
   */
  chatVisionAvailable?: boolean;
  /**
   * Chat image-generation availability (2026-07-07). Server-computed like
   * `chatVisionAvailable`: the deployment flag AND a live non-sovereign
   * image-gen backend present in this deployment's Meridian catalog. Fails soft
   * to `false` — when absent or on a load error the composer hides/disables the
   * image-gen affordance rather than routing into a dead end.
   */
  chatImageGenAvailable?: boolean;
  /**
   * Deck-editor AI slide-image availability (DECK-3, 2026-07-27). Server-computed
   * like the two above: the `deck_editor` flag AND all three server seams wired
   * (generator + asset sealer + DEK resolver) AND a live paperbanana worker
   * heartbeat. Distinct from `chatImageGenAvailable` — the deck path can be dark
   * while chat's is live (separate flag, and sealing needs the deck asset routes).
   * Fails soft to `false` so the editor shows an upload-only control rather than
   * a button that dead-ends after spending credits.
   */
  deckImageGenAvailable?: boolean;
  /**
   * DECK-13, per viewer: the caller's saved floor is Sovereign or stricter and no
   * waiver is in force (or the floor or waiver read failed), so a deck image
   * generation would be refused. The editor then shows a locked button linking to
   * the Privacy waiver control. Absent on an older backend ⇒ false; the server
   * still refuses with 204, whose toast routes to the same control.
   */
  deckImageGenNeedsConsent?: boolean;
  /**
   * Deck-editor PDF-export availability: the render backend (export-worker or
   * in-process chromium) AND the deck asset reader AND the DEK resolver all
   * wired. Distinct from `deckImageGenAvailable` — PDF needs the export-worker,
   * image gen needs paperbanana; either can be dark while the other is live, so
   * one flag cannot stand in for the other. Fails soft to `false` so the editor
   * hides an export that would only 503.
   */
  deckPdfExportAvailable?: boolean;
  /**
   * The deployment stage: deployenv.Classify(LVDR_ENVIRONMENT) on the server —
   * `development` | `preprod` | `production`, `unknown`, or a sanitised
   * unrecognised value. Drives features/stage-marker. Absent on a backend older
   * than 2026-09-14, which reads as '' (no marker).
   */
  environment?: string;
  /**
   * INBOX-17: who the server answered for. `user` when the request carried a valid session (the per-user flags
   * are in `features`), `anonymous` when it did not. Absent on an older backend, which reads as `user` under a
   * signed-in identity so that backend still settles.
   */
  viewer?: 'user' | 'anonymous';
  /**
   * LIMIT-05: the limits the server enforces for this caller, keyed by limit
   * key ("chat.title"). Read through useLimit / CappedInput. Absent on an older
   * backend, which reads as {} ("not reported"): no counter and no client cap,
   * never a number of our own. Parsed defensively by parseLimits.
   */
  limits?: unknown;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

interface FeatureFlagsStore {
  bundle: string;
  features: Record<string, FeatureState>;
  /** Chat vision-input availability (server-computed; see FeaturesResponse). */
  chatVisionAvailable: boolean;
  /** Chat image-generation availability (server-computed; see FeaturesResponse). */
  chatImageGenAvailable: boolean;
  /** Deck-editor AI slide-image availability (server-computed; see FeaturesResponse). */
  deckImageGenAvailable: boolean;
  /** Deck image generation needs the image waiver (server-computed per viewer; see FeaturesResponse). */
  deckImageGenNeedsConsent: boolean;
  /** Deck-editor PDF-export availability (server-computed; see FeaturesResponse). */
  deckPdfExportAvailable: boolean;
  /** Deployment stage (server-computed; see FeaturesResponse). */
  environment: string;
  /** LIMIT-05 (see FeaturesResponse.limits). */
  limits: Record<string, ReportedLimit>;
  loadState: LoadState;
  /**
   * INBOX-17: the identity key (`identityKey`) the current `features` answer was fetched for. `inbox_intelligence`
   * and `inbox_triage` are per-user, so an answer for one identity is not an answer for another.
   */
  answeredFor: string | null;
  /**
   * The current identity key, mirrored from the auth store by `bindFeatureFlagsToAuth`. `null` while auth is still
   * resolving, and before the binding exists (a page outside the app shell).
   */
  identity: string | null;
  /**
   * A signed-in fetch was answered `viewer: 'anonymous'`; the next access-token change retries it. Stays set after
   * the fail-closed settle (see `ANONYMOUS_RETRY_MS`), so a token that arrives late still gets the user's answer.
   */
  pendingViewerRetry: boolean;
  actions: {
    /**
     * Fetches /api/v1/features once and populates the store. Idempotent: a
     * second call while loading or after success is a no-op. The runtime
     * delivery is what lets ONE FE build render correctly for both the release
     * and beta deployments (R3) — feature visibility comes from the server's
     * resolved bundle, not from build-time NEXT_PUBLIC_* inlining.
     */
    load: () => Promise<void>;
    /**
     * Force-refetches the snapshot, bypassing load()'s idempotency guard.
     * Called by the WS dispatcher when the backend pushes a
     * FeaturesUpdatedEvent (admin flipped a flag). Keeps prior state on
     * transient error — a mid-session GET failure shouldn't degrade the
     * working session by suddenly hiding every feature. The exception is an
     * error while the store holds no answer for the CURRENT identity (sign-out,
     * user switch): that clears `features`, fail-closed, because the prior
     * answer belongs to someone else.
     */
    refresh: () => Promise<void>;
  };
}

/**
 * The identity a features answer belongs to: `user:<id>` for a signed-in session (`user:unknown` when the session
 * has a token but no user object yet, so it still settles), `anon` once auth resolved signed-out, `null` while auth
 * is still resolving (no answer counts as settled then).
 */
export function identityKey(s: { status: string; user: { id: string } | null }): string | null {
  if (s.status === 'authenticated') return `user:${s.user?.id ?? 'unknown'}`;
  if (s.status === 'unauthenticated') return 'anon';
  return null;
}

/** What this store reads of the auth store. Structural, so this module never imports authStore (see the binding). */
interface AuthSnapshot {
  status: string;
  user: { id: string } | null;
  accessToken: string | null;
}
interface AuthSource {
  getState: () => AuthSnapshot;
  subscribe: (listener: (next: AuthSnapshot, prev: AuthSnapshot) => void) => () => void;
}

/** The bound auth store; null until `bindFeatureFlagsToAuth` runs. */
let authSource: AuthSource | null = null;
let authUnsub: (() => void) | null = null;
const NO_AUTH: AuthSnapshot = { status: 'idle', user: null, accessToken: null };
const authNow = (): AuthSnapshot => authSource?.getState() ?? NO_AUTH;

/** Bumped by the test reset, so a fetch started before it can never land after it. */
let generation = 0;

/** Latest-wins: an answer lands only if no newer fetch started after its own (two pushes in quick succession). */
let fetchSeq = 0;

/**
 * How long a signed-in session that was answered `viewer: 'anonymous'` waits for a new access token before it asks
 * once more with the token it has. A second anonymous answer for the same token then settles, fail-closed, so no
 * session stays unsettled forever. Long enough for the boot token refresh to land first.
 */
export const ANONYMOUS_RETRY_MS = 3_000;

/** The identity and token the last unaccepted anonymous answer was given for; null when there is none. */
let anonymousAnswer: { key: string; token: string | null } | null = null;
let anonymousRetryTimer: ReturnType<typeof setTimeout> | null = null;
const clearAnonymousRetry = () => {
  if (anonymousRetryTimer !== null) clearTimeout(anonymousRetryTimer);
  anonymousRetryTimer = null;
};

export const useFeatureFlagsStore = create<FeatureFlagsStore>((set, get) => {
  /**
   * One fetch of /api/v1/features for the identity current at request start. The answer lands only if that
   * identity is still current, no newer fetch started, AND the server answered for it (`viewer`); otherwise it is
   * dropped and the auth subscription (identity change), the newer fetch or the token retry supplies the answer.
   */
  const fetchSnapshot = async (kind: 'load' | 'refresh'): Promise<void> => {
    const gen = generation;
    const seq = ++fetchSeq;
    const auth = authNow();
    const key = identityKey(auth);
    const token = auth.accessToken;
    const fail = () => {
      if (gen !== generation || seq !== fetchSeq || identityKey(authNow()) !== key) return;
      if (kind === 'load' || get().answeredFor !== key || get().loadState === 'loading') {
        // Fail-closed: leave features empty so useFeature() returns false for
        // everything. A disabled-by-default posture is the safe failure mode —
        // better to hide a feature than to surface a UI whose API is gated off.
        // The same holds when the answer we hold is another identity's, and
        // when this refetch overtook a first load that therefore never lands.
        // `limits` clears with it (LIMIT-05): another identity's limits must
        // not survive a sign-out, and "not reported" shows no counter.
        clearAnonymousRetry();
        anonymousAnswer = null;
        set({
          features: {},
          limits: {},
          loadState: 'error',
          answeredFor: key,
          pendingViewerRetry: false,
        });
      }
      // Else intentional: keep prior `features` and `bundle`. loadState stays
      // 'ready' from the prior successful load — a transient refetch
      // failure shouldn't silently hide every feature in a working
      // session (and the next push will retry).
    };
    let resp: FeaturesResponse;
    try {
      resp = await apiFetch<FeaturesResponse>('/api/v1/features');
    } catch {
      fail();
      return;
    }
    if (typeof resp !== 'object' || resp === null) {
      fail();
      return;
    }
    if (gen !== generation || seq !== fetchSeq) return;
    const now = authNow();
    // Fetched for an identity that is no longer current: the auth subscription has already asked again.
    if (identityKey(now) !== key) return;
    // A /demo guest is the exception to "anonymous is not a signed-in user's answer": the server answers a valid
    // demo token anonymously on purpose, and that token never refreshes. The anonymous snapshot is the guest's own.
    const anonymousWhileSignedIn =
      key !== null &&
      key.startsWith('user:') &&
      resp.viewer === 'anonymous' &&
      !isDemoGuestAccessToken(token);
    let pendingViewerRetry = false;
    if (anonymousWhileSignedIn) {
      // Signed in, but the server saw no valid session (the token expired in flight). The token refresh that
      // follows retries; if it already happened while this request was out, retry now (rev2 I3).
      if (now.accessToken !== token) {
        void get().actions.refresh();
        return;
      }
      if (anonymousAnswer?.key !== key || anonymousAnswer.token !== token) {
        // First anonymous answer for this token: not the user's. Wait for a new token, and ask once more if none
        // comes.
        anonymousAnswer = { key, token };
        set({ pendingViewerRetry: true });
        clearAnonymousRetry();
        anonymousRetryTimer = setTimeout(() => {
          anonymousRetryTimer = null;
          const a = authNow();
          if (gen !== generation || identityKey(a) !== key || a.accessToken !== token) return;
          if (get().answeredFor !== key) void get().actions.refresh();
        }, ANONYMOUS_RETRY_MS);
        return;
      }
      // The same answer again for the same token: the server will not answer this session as a user. Settle on the
      // anonymous snapshot, in which the per-user features are off. A new token still retries.
      pendingViewerRetry = true;
    } else {
      anonymousAnswer = null;
    }
    clearAnonymousRetry();
    const map: Record<string, FeatureState> = {};
    for (const f of resp.features ?? []) map[f.id] = f;
    set({
      bundle: resp.bundle ?? '',
      features: map,
      chatVisionAvailable: resp.chatVisionAvailable ?? false,
      chatImageGenAvailable: resp.chatImageGenAvailable ?? false,
      deckImageGenAvailable: resp.deckImageGenAvailable ?? false,
      deckImageGenNeedsConsent: resp.deckImageGenNeedsConsent ?? false,
      deckPdfExportAvailable: resp.deckPdfExportAvailable ?? false,
      // Typed as string, but it is a wire field: a number here would throw
      // in markerFor() inside the root-mounted StageMarker, on every route.
      environment: typeof resp.environment === 'string' ? resp.environment : '',
      limits: parseLimits(resp.limits),
      loadState: 'ready',
      answeredFor: key,
      pendingViewerRetry,
    });
  };

  return {
    bundle: '',
    features: {},
    chatVisionAvailable: false,
    chatImageGenAvailable: false,
    deckImageGenAvailable: false,
    deckImageGenNeedsConsent: false,
    deckPdfExportAvailable: false,
    environment: '',
    limits: {},
    loadState: 'idle',
    answeredFor: null,
    identity: null,
    pendingViewerRetry: false,
    actions: {
      load: async () => {
        const st = get().loadState;
        if (st === 'loading' || st === 'ready') return;
        set({ loadState: 'loading' });
        await fetchSnapshot('load');
      },

      // Note: we deliberately do NOT flip loadState to 'loading' here. Unlike
      // load() (which transitions idle → loading → ready), refresh() runs
      // while the store is already 'ready' and the user is mid-session. A
      // loading flicker would briefly invalidate consumers' view of the
      // current feature set, causing UI jitter for a push that's typically
      // a no-change confirmation. Background fetch in-flight = silent. An
      // identity change needs no flag either: `useFeatureFlagsSettled` is
      // false until `answeredFor` matches the new identity.
      refresh: async () => {
        await fetchSnapshot('refresh');
      },
    },
  };
});

/**
 * Binds the store to the auth store: refetch when the identity changes (sign-in, sign-out, user switch), and retry
 * an answer the server gave anonymously while signed in once a new access token lands. Idempotent. Called from
 * AppConnection, which passes `useAuthStore` in.
 *
 * Injected, not imported: authStore sits inside an import cycle (authStore → hardLogout → workspaceStore →
 * authStore) and evaluates `@/lib/http` exports at module load, while this module is imported by nearly every
 * feature. Until the binding exists the identity is `null`, so nothing reads as settled and no answer is recorded
 * as a user's; binding then fetches for the identity it finds.
 */
export function bindFeatureFlagsToAuth(source: AuthSource): void {
  if (authUnsub) return;
  authSource = source;
  authUnsub = source.subscribe((next, prev) => {
    const key = identityKey(next);
    const { actions, pendingViewerRetry, loadState } = useFeatureFlagsStore.getState();
    if (key !== identityKey(prev)) {
      useFeatureFlagsStore.setState({ identity: key });
      // Still resolving (`null`): nothing to fetch for yet; the settled identity triggers the fetch. `idle`: nobody
      // has asked for flags yet, and load() fetches for the identity current then.
      if (key !== null && loadState !== 'idle') void actions.refresh();
      return;
    }
    if (pendingViewerRetry && next.accessToken !== prev.accessToken) void actions.refresh();
  });
  const key = identityKey(source.getState());
  const st = useFeatureFlagsStore.getState();
  useFeatureFlagsStore.setState({ identity: key });
  // An answer fetched before the binding (the root-mounted StageMarker loads on every route) belongs to no known
  // identity. `idle` is left to load(), as above.
  if (key !== null && st.answeredFor !== key && st.loadState !== 'idle') {
    void st.actions.refresh();
  }
}

/** Test-only: drop every in-flight fetch and the auth binding. */
export function __resetFeatureFlagsForTests(): void {
  generation++;
  clearAnonymousRetry();
  anonymousAnswer = null;
  authUnsub?.();
  authUnsub = null;
  authSource = null;
  useFeatureFlagsStore.setState({ answeredFor: null, identity: null, pendingViewerRetry: false });
}

/**
 * Reactive hook: returns whether a catalog feature is enabled in this
 * deployment. Unknown / not-yet-loaded ids return false (fail-closed).
 */
export function useFeature(id: string): boolean {
  return useFeatureFlagsStore((s) => s.features[id]?.enabled ?? false);
}

/**
 * Reactive hook: whether the flag answer for the CURRENT identity has SETTLED
 * (succeeded or failed). `useFeature` is false both for "off" and for "not
 * loaded yet"; a component whose off-state renders something visibly different
 * (a raw code block, a redirect) uses this to show a neutral placeholder until
 * the answer is known, instead of flashing the off-state on every page load.
 * INBOX-17: an anonymous answer does not settle a signed-in session, and
 * nothing settles while auth is still resolving.
 */
export function useFeatureFlagsSettled(): boolean {
  return useFeatureFlagsStore(
    (s) =>
      (s.loadState === 'ready' || s.loadState === 'error') &&
      s.identity !== null &&
      s.answeredFor === s.identity,
  );
}

/**
 * Non-reactive getter for code paths outside React render (event handlers,
 * module-level checks). Prefer useFeature inside components so the UI updates
 * when flags load.
 */
export function getFeature(id: string): boolean {
  return useFeatureFlagsStore.getState().features[id]?.enabled ?? false;
}

/**
 * Reactive hook: whether chat vision-input (image attachments) is available in
 * this deployment — the server ANDs the deployment flag with a live sovereign
 * vision backend. Fails closed (false) until the bootstrap load resolves. This
 * is the sole gate for the composer's attachment affordance — there is no
 * build-time flag; visibility comes entirely from this runtime signal.
 */
export function useChatVisionAvailable(): boolean {
  return useFeatureFlagsStore((s) => s.chatVisionAvailable);
}

/**
 * Reactive hook: whether chat image-generation is available in this deployment
 * — the server ANDs the deployment flag with a live non-sovereign image-gen
 * backend. Fails closed (false) until the bootstrap load resolves. Pair with
 * the composer's build gate.
 */
export function useChatImageGenAvailable(): boolean {
  return useFeatureFlagsStore((s) => s.chatImageGenAvailable);
}

/**
 * Reactive hook: whether the deck editor's AI slide-image generation is
 * available — the server ANDs the `deck_editor` flag, all three server-side
 * seams, and the paperbanana worker heartbeat. Fails closed (false) until the
 * bootstrap load resolves, so the editor offers upload-only until proven
 * otherwise.
 */
export function useDeckImageGenAvailable(): boolean {
  return useFeatureFlagsStore((s) => s.deckImageGenAvailable);
}

/**
 * Reactive hook (DECK-13): whether the viewer's deck image generation needs the
 * image sovereignty waiver first. False until the bootstrap load resolves and on
 * an older backend; the server's 204 is the backstop either way.
 */
export function useDeckImageGenNeedsConsent(): boolean {
  return useFeatureFlagsStore((s) => s.deckImageGenNeedsConsent);
}

/**
 * Deck-editor PDF export: the `deck_editor` flag AND the render backend + asset
 * reader + DEK resolver all wired server-side. Fails closed (false) until the
 * bootstrap load resolves, so the editor hides the button rather than offering
 * an export that would 503 after the user waits on a chromium print.
 */
export function useDeckPdfExportAvailable(): boolean {
  return useFeatureFlagsStore((s) => s.deckPdfExportAvailable);
}

/** Triggers the one-time features fetch. Safe to call from a mount effect. */
export function loadFeatureFlags(): void {
  void useFeatureFlagsStore.getState().actions.load();
}
